import React, { useMemo } from "react";
import { Box, Typography, Alert } from "@mui/material";
import ConfigurableBarChart from "../../ConfigurableBarChart";
import LoadingSpinner from "../../../common/LoadingSpinner";
import EmptyChartState from "../../../common/EmptyChartState";
import { getTimePeriodTitleSuffix } from "@utils/time-period-display";
import { resolveTimePeriodInWindow, getTimePeriodDates } from "@utils/time-period-utils";
import type { TimePeriod } from "@store/slices/chart-config-slice";
import {
  getRollingAverageFooterText,
  getRollingAverageComparisonFooterText,
} from "@utils/rolling-average-utils";
import type { BarChartConfig } from "@store/slices/chart-config-slice";

interface PreviewPanelProps {
  currentConfig: BarChartConfig;
  questions: any;
  isRefetchingData: boolean;
  isAvailableData: boolean;
  hasSampleSizeBelowThreshold: boolean | undefined;
  hasPartialHistoricalData: boolean | undefined;
  hasNoPreviousPeriodData: boolean | undefined;
  dashboardDateOverride?: { startDate: string; endDate: string };
  dataAvailabilityDate?: string | null;
}

const PreviewPanel: React.FC<PreviewPanelProps> = ({
  currentConfig,
  questions,
  isRefetchingData,
  isAvailableData,
  hasSampleSizeBelowThreshold,
  hasPartialHistoricalData,
  hasNoPreviousPeriodData,
  dashboardDateOverride,
  dataAvailabilityDate,
}) => {
  const isNoPeriodAvailable = useMemo(() => {
    if (!dashboardDateOverride?.startDate || !dashboardDateOverride?.endDate)
      return false;
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod as
      | TimePeriod
      | undefined;
    if (
      !timePeriod ||
      timePeriod === "all_time" ||
      timePeriod === "custom_period"
    )
      return false;
    return !resolveTimePeriodInWindow(
      timePeriod,
      dashboardDateOverride.startDate,
      dashboardDateOverride.endDate,
    );
  }, [dashboardDateOverride, currentConfig.timePeriodSelection?.timePeriod]);

  const noPeriodMessage = useMemo(() => {
    if (!isNoPeriodAvailable) return null;
    const timePeriod = currentConfig.timePeriodSelection?.timePeriod ?? "";
    const unit = timePeriod.includes("semester") ? "semester" : "quarter";
    return `No complete ${unit} available within the selected date range.`;
  }, [isNoPeriodAvailable, currentConfig.timePeriodSelection?.timePeriod]);

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        p: 3,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        Preview
      </Typography>
      <Box
        sx={{
          flex: 1,
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          minHeight: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isRefetchingData ? (
          <LoadingSpinner message="Loading data..." />
        ) : isAvailableData ? (
          <EmptyChartState message="This question has no response data available for visualization" />
        ) : isNoPeriodAvailable ? (
          <EmptyChartState message={noPeriodMessage!} />
        ) : (
          <ConfigurableBarChart dashboardDateOverride={dashboardDateOverride} />
        )}
      </Box>

      {/* Footer Info */}
      <Box
        sx={{
          mt: 2,
          p: 1,
          px: 2.5,
          backgroundColor: "#f9fafb",
          borderRadius: 1,
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {currentConfig.visualizationBase?.enabled &&
            currentConfig.visualizationBase.name && (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
              >
                Base: {currentConfig.visualizationBase.name}
              </Typography>
            )}
          <TimePeriodFooter
            currentConfig={currentConfig}
            questions={questions}
            dataAvailabilityDate={dataAvailabilityDate}
          />
          <RollingAverageFooter
            currentConfig={currentConfig}
            questions={questions}
            dataAvailabilityDate={dataAvailabilityDate}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          Weight:{" "}
          {currentConfig.weight === "DISABLED" ? "No Weight" : "Default Weight"}
        </Typography>
      </Box>

      {/* Sample Size Warning */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
        }}
      >
        {hasNoPreviousPeriodData && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            No data available for the{" "}
            {currentConfig.rollingAverage?.enabled
              ? "previous rolling window"
              : "previous time period"}{" "}
            to perform statistical testing.
          </Alert>
        )}

        {currentConfig.rollingAverage?.enabled &&
          currentConfig.rollingAverage?.windowSize !== undefined &&
          (currentConfig as any).effectiveRollingPeriodCount !== undefined &&
          currentConfig.rollingAverage.windowSize >
            (currentConfig as any).effectiveRollingPeriodCount && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              Warning: Insufficient historical data for selected rolling window.
              Available periods:{" "}
              {(currentConfig as any).effectiveRollingPeriodCount}. Rolling
              window reduced to{" "}
              {(currentConfig as any).effectiveRollingPeriodCount} periods.
            </Alert>
          )}

        {hasSampleSizeBelowThreshold && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {currentConfig.rollingAverage?.enabled
              ? "Warning: Weighted base for one or more rolling windows is below the recommended threshold for reliable statistical testing."
              : "Warning: Sample size for one or more time periods is below the recommended threshold for reliable statistical testing."}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

function TimePeriodFooter({
  currentConfig,
  questions,
  dataAvailabilityDate,
}: {
  currentConfig: BarChartConfig;
  questions: any;
  dataAvailabilityDate?: string | null;
}) {
  const timePeriodSelection = currentConfig.timePeriodSelection;

  if (!timePeriodSelection || timePeriodSelection.timePeriod === "all_time")
    return null;

  const hasCustomTitle =
    currentConfig.title !== "" &&
    currentConfig.title !== "Brand Tracking" &&
    questions &&
    currentConfig.title !== questions.name;

  if (!hasCustomTitle) return null;

  const tp = timePeriodSelection.timePeriod;
  const isNamedPeriod =
    tp === "latest_quarter" ||
    tp === "previous_quarter" ||
    tp === "latest_semester" ||
    tp === "previous_semester";
  const timePeriodSuffix = getTimePeriodTitleSuffix(
    tp,
    isNamedPeriod ? null : timePeriodSelection.startDate,
    isNamedPeriod ? null : timePeriodSelection.endDate,
    dataAvailabilityDate ?? undefined,
  );

  if (!timePeriodSuffix) return null;

  const timePeriodText = timePeriodSuffix.startsWith(" - ")
    ? timePeriodSuffix.substring(3)
    : timePeriodSuffix;

  return (
    <Typography variant="caption" color="text.secondary" fontWeight={500}>
      Time Period: {timePeriodText}
    </Typography>
  );
}

function RollingAverageFooter({
  currentConfig,
  questions,
  dataAvailabilityDate,
}: {
  currentConfig: BarChartConfig;
  questions: any;
  dataAvailabilityDate?: string | null;
}) {
  if (
    !currentConfig.rollingAverage?.enabled ||
    !currentConfig.rollingAverage.windowSize
  ) {
    return null;
  }

  const timePeriod = currentConfig.timePeriodSelection?.timePeriod;
  if (!timePeriod) return null;

  // Only show rolling average period info when title is customized
  const hasCustomTitle =
    currentConfig.title !== "" &&
    currentConfig.title !== "Brand Tracking" &&
    questions &&
    currentConfig.title !== questions.name;

  const isNamedPeriod =
    timePeriod === "latest_quarter" ||
    timePeriod === "previous_quarter" ||
    timePeriod === "latest_semester" ||
    timePeriod === "previous_semester";

  // For named periods, resolve fresh dates from the current availability cutoff so
  // stale saved start/end dates don't cause the wrong quarter to appear.
  let periodStart = currentConfig.timePeriodSelection?.startDate ?? null;
  let periodEnd = currentConfig.timePeriodSelection?.endDate ?? null;
  if (isNamedPeriod) {
    const fresh = getTimePeriodDates(timePeriod, dataAvailabilityDate ?? undefined);
    if (fresh) {
      periodStart = fresh.startDate;
      periodEnd = fresh.endDate;
    }
  }

  const footerText = hasCustomTitle
    ? getRollingAverageFooterText(
        currentConfig.rollingAverage.windowSize,
        timePeriod,
        periodStart,
        periodEnd,
      )
    : null;

  const comparisonText = getRollingAverageComparisonFooterText(
    currentConfig.rollingAverage.windowSize,
    timePeriod,
    periodStart,
    periodEnd,
  );

  return (
    <>
      {footerText && (
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {footerText}
        </Typography>
      )}
      {comparisonText && (
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {comparisonText}
        </Typography>
      )}
    </>
  );
}

export default PreviewPanel;
